'use client';
import Hls from 'hls.js';
import { memo, useEffect } from 'react';

import { usePlayerCurrentTimeObserver } from '../hooks/usePlayerCurrentTimeObserver';
import { usePlayerStore, usePlayerStoreApi } from './PlayerContextProvider';

export type PlayerSyncProps = {
  onTimeUpdate?: (currentTime: number) => void;
};

export const PlayerSync = memo(({ onTimeUpdate }: PlayerSyncProps) => {
  const playerStoreApi = usePlayerStoreApi();
  const captionsEnabled = usePlayerStore((state) => state.captionsEnabled);
  const hlsInstance = usePlayerStore((state) => state.hlsInstance);
  const initialized = usePlayerStore((state) => state.initialized);
  const muted = usePlayerStore((state) => state.muted);
  const playbackRate = usePlayerStore((state) => state.playbackRate);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const setDuration = usePlayerStore((state) => state.setDuration);
  const setInitialized = usePlayerStore((state) => state.setInitialized);
  const setLoop = usePlayerStore((state) => state.setLoop);
  const setMuted = usePlayerStore((state) => state.setMuted);
  const setPlaybackRate = usePlayerStore((state) => state.setPlaybackRate);
  const setPlaying = usePlayerStore((state) => state.setPlaying);
  const setReady = usePlayerStore((state) => state.setReady);
  const setResolutionLevel = usePlayerStore((state) => state.setResolutionLevel);
  const setResolutionLevels = usePlayerStore((state) => state.setResolutionLevels);
  const setSeeking = usePlayerStore((state) => state.setSeeking);
  const setSubtitleTrack = usePlayerStore((state) => state.setSubtitleTrack);
  const setSubtitleTracks = usePlayerStore((state) => state.setSubtitleTracks);
  const setVolume = usePlayerStore((state) => state.setVolume);
  const videoElement = usePlayerStore((state) => state.videoElement, {
    dangerouslyIncludeEventListeners: true,
  });
  const volume = usePlayerStore((state) => state.volume);
  const { startObservingCurrentTime, stopObservingCurrentTime } = usePlayerCurrentTimeObserver({
    interval: 50,
    onTimeUpdate,
  });

  useEffect(() => {
    if (!initialized && videoElement) {
      setInitialized(true);

      videoElement.volume = volume;
      videoElement.muted = muted;

      // HTMLVideoElement playback rate can not be a negative value
      if (playbackRate >= 0) {
        videoElement.playbackRate = playbackRate;
      } else {
        setPlaybackRate(1);
      }
    }
  }, [videoElement, muted, playbackRate, volume, initialized, setInitialized, setPlaybackRate]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleLoadMetadata = () => {
      setCurrentTime(videoElement.currentTime);
      setDuration(videoElement.duration);
      setLoop(videoElement.loop);
      setVolume(videoElement.volume);
      setMuted(videoElement.muted);
      setReady(true);
    };

    videoElement.addEventListener('loadedmetadata', handleLoadMetadata);

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadMetadata);
    };
  }, [videoElement, setCurrentTime, setDuration, setLoop, setMuted, setVolume, setReady]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handlePause = () => {
      stopObservingCurrentTime();
      setPlaying(false);
    };

    videoElement.addEventListener('pause', handlePause);

    return () => {
      videoElement.removeEventListener('pause', handlePause);
    };
  }, [videoElement, setPlaying, stopObservingCurrentTime]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handlePlaybackRateChange = () => {
      const { playbackRate, transientPlaybackRate } = playerStoreApi.getState();

      // Reverse playback uses a simulated clock; queued native events must not overwrite that selected speed.
      if (playbackRate <= 0 || videoElement.playbackRate === transientPlaybackRate) {
        return;
      }

      setPlaybackRate(videoElement.playbackRate);
    };

    videoElement.addEventListener('ratechange', handlePlaybackRateChange);

    return () => {
      videoElement.removeEventListener('ratechange', handlePlaybackRateChange);
    };
  }, [playerStoreApi, videoElement, setPlaybackRate]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handlePlay = () => {
      startObservingCurrentTime();
      setPlaying(true);
    };

    videoElement.addEventListener('play', handlePlay);

    return () => {
      videoElement.removeEventListener('play', handlePlay);
    };
  }, [videoElement, setPlaying, startObservingCurrentTime]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleDurationChange = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener('durationchange', handleDurationChange);

    return () => {
      videoElement.removeEventListener('durationchange', handleDurationChange);
    };
  }, [videoElement, setDuration]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
      onTimeUpdate?.(videoElement.currentTime);
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoElement, setCurrentTime, onTimeUpdate]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleSeeked = () => {
      setSeeking(false);
    };

    videoElement.addEventListener('seeked', handleSeeked);

    return () => {
      videoElement.removeEventListener('seeked', handleSeeked);
    };
  }, [videoElement, setSeeking]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleSeeking = () => {
      setSeeking(true);
    };

    videoElement.addEventListener('seeking', handleSeeking);

    return () => {
      videoElement.removeEventListener('seeking', handleSeeking);
    };
  }, [videoElement, setSeeking]);

  useEffect(() => {
    if (!videoElement) {
      return;
    }

    const handleVolumeChange = () => {
      setMuted(videoElement.muted);
      setVolume(videoElement.volume);
    };

    videoElement.addEventListener('volumechange', handleVolumeChange);

    return () => {
      videoElement.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [setVolume, setMuted, videoElement]);

  useEffect(() => {
    if (!hlsInstance) {
      setSubtitleTracks([]);
      setSubtitleTrack(-1);
      return;
    }

    const handleSubtitleTracksUpdated = () => {
      const tracks = hlsInstance.subtitleTracks.map((track) => ({
        id: track.id,
        lang: track.lang,
        name: track.name,
      }));
      setSubtitleTracks(tracks);

      if (captionsEnabled && tracks.length > 0) {
        // Restore captions preference when navigating to a new asset.
        hlsInstance.subtitleTrack = 0;
      } else {
        setSubtitleTrack(hlsInstance.subtitleTrack);
      }
    };

    const handleSubtitleTrackSwitch = (_event: typeof Hls.Events.SUBTITLE_TRACK_SWITCH, data: { id: number }) => {
      setSubtitleTrack(data.id);
    };

    hlsInstance.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);
    hlsInstance.on(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleTrackSwitch);

    // Initialize with current state if tracks are already loaded
    if (hlsInstance.subtitleTracks.length > 0) {
      handleSubtitleTracksUpdated();
    }

    return () => {
      hlsInstance.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);
      hlsInstance.off(Hls.Events.SUBTITLE_TRACK_SWITCH, handleSubtitleTrackSwitch);
    };
  }, [captionsEnabled, hlsInstance, setSubtitleTrack, setSubtitleTracks]);

  useEffect(() => {
    if (!hlsInstance) {
      setResolutionLevels([]);
      setResolutionLevel(-1);
      return;
    }

    const handleManifestParsed = () => {
      const levels = hlsInstance.levels.map((level, index) => ({
        height: level.height,
        id: index,
        width: level.width,
      }));
      setResolutionLevels(levels);
      setResolutionLevel(hlsInstance.currentLevel);
    };

    const handleLevelSwitched = (_event: typeof Hls.Events.LEVEL_SWITCHED, data: { level: number }) => {
      setResolutionLevel(data.level);
    };

    hlsInstance.on(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
    hlsInstance.on(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);

    // Initialize with current state if levels are already loaded
    if (hlsInstance.levels.length > 0) {
      handleManifestParsed();
    }

    return () => {
      hlsInstance.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
      hlsInstance.off(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
    };
  }, [hlsInstance, setResolutionLevel, setResolutionLevels]);

  return null;
});

PlayerSync.displayName = 'PlayerSync';
